import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Calendar, CheckCircle, AlertCircle, Clock, ArrowRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TermManagementPage() {
  const [activeTerm, setActiveTerm] = useState("Term 1");
  const [academicYear, setAcademicYear] = useState(new Date().getFullYear().toString());
  const [termStatus, setTermStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [endingTerm, setEndingTerm] = useState(false);
  const [endingYear, setEndingYear] = useState(false);
  const [showEndTermDialog, setShowEndTermDialog] = useState(false);
  const [showEndYearDialog, setShowEndYearDialog] = useState(false);
  const [selectedNewTerm, setSelectedNewTerm] = useState("");

  useEffect(() => {
    fetchSettings();
  }, []);

  useEffect(() => {
    if (activeTerm) {
      fetchTermStatus(activeTerm);
    }
  }, [activeTerm]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings/active-term`);
      setActiveTerm(response.data.active_term || "Term 1");
      setAcademicYear(response.data.academic_year || new Date().getFullYear().toString());
    } catch (error) {
      console.error("Failed to fetch settings:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTermStatus = async (term) => {
    try {
      const response = await axios.get(`${API}/results/term-status/${encodeURIComponent(term)}`);
      setTermStatus(response.data);
    } catch (error) {
      console.error("Failed to fetch term status:", error);
    }
  };

  const handleSetActiveTerm = async () => {
    if (!selectedNewTerm) return;
    try {
      await axios.put(`${API}/settings/active-term`, {
        active_term: selectedNewTerm,
        academic_year: academicYear,
      });
      toast.success(`Active term set to ${selectedNewTerm}`);
      setActiveTerm(selectedNewTerm);
      setSelectedNewTerm("");
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update term");
    }
  };

  const handleEndTerm = async () => {
    setEndingTerm(true);
    try {
      const response = await axios.post(`${API}/settings/end-term`);
      toast.success(response.data.message);
      setShowEndTermDialog(false);
      setActiveTerm(response.data.active_term);
      fetchTermStatus(response.data.active_term);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to end term");
    } finally {
      setEndingTerm(false);
    }
  };

  const handleEndYear = async () => {
    setEndingYear(true);
    try {
      const response = await axios.post(`${API}/settings/end-year`);
      toast.success(response.data.message);
      setShowEndYearDialog(false);
      setActiveTerm(response.data.active_term);
      setAcademicYear(response.data.academic_year);
      fetchTermStatus(response.data.active_term);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to end year");
    } finally {
      setEndingYear(false);
    }
  };

  const termNum = parseInt(activeTerm.replace("Term ", ""));

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  return (
    <div data-testid="term-management-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="font-heading font-bold text-3xl text-slate-900 flex items-center gap-3">
          <Calendar className="text-pink-500" size={32} />
          Term Management
        </h1>
        <p className="text-slate-500 mt-1">Manage academic terms and year transitions</p>
      </div>

      {/* Current Status */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card className="border-slate-200 border-l-4 border-l-pink-500">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500 mb-1">Active Term</p>
            <p className="text-3xl font-heading font-bold text-slate-900">{activeTerm}</p>
            <Badge className="mt-2 bg-pink-100 text-pink-700">{academicYear}</Badge>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-6">
            <p className="text-sm text-slate-500 mb-1">Term Progress</p>
            <div className="flex items-center gap-3 mt-2">
              {["Term 1", "Term 2", "Term 3"].map((term, idx) => (
                <div key={term} className="flex items-center gap-2">
                  <div
                    className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold ${
                      term === activeTerm
                        ? "bg-pink-500 text-white"
                        : idx < termNum - 1
                        ? "bg-emerald-100 text-emerald-700"
                        : "bg-slate-100 text-slate-400"
                    }`}
                  >
                    {idx + 1}
                  </div>
                  {idx < 2 && (
                    <ArrowRight size={16} className="text-slate-300" />
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Set Active Term */}
      <Card className="border-slate-200 mb-6">
        <CardHeader>
          <CardTitle className="font-heading flex items-center gap-2">
            <Clock className="text-pink-500" size={20} />
            Declare Active Term
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-slate-600 mb-4">
            Set which term is currently active. Teachers can only enter results for the active term.
          </p>
          <div className="flex items-end gap-4">
            <div className="flex-1 max-w-xs">
              <Select value={selectedNewTerm} onValueChange={setSelectedNewTerm}>
                <SelectTrigger className="border-slate-200">
                  <SelectValue placeholder="Select term to activate" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Term 1">Term 1</SelectItem>
                  <SelectItem value="Term 2">Term 2</SelectItem>
                  <SelectItem value="Term 3">Term 3</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleSetActiveTerm}
              disabled={!selectedNewTerm || selectedNewTerm === activeTerm}
              className="bg-pink-500 hover:bg-pink-600 text-white"
            >
              Set Active
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Term Status */}
      {termStatus && (
        <Card className="border-slate-200 mb-6">
          <CardHeader>
            <CardTitle className="font-heading">
              {activeTerm} Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-slate-50 rounded">
              <div className="flex items-center gap-2">
                {termStatus.all_results_entered ? (
                  <CheckCircle className="text-emerald-500" size={20} />
                ) : (
                  <AlertCircle className="text-amber-500" size={20} />
                )}
                <span className="font-medium text-slate-700">Results Status</span>
              </div>
              <span className="text-sm text-slate-600">
                {termStatus.students_with_results} / {termStatus.total_students} students
                ({termStatus.completion_percentage}%)
              </span>
            </div>

            <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
              <div
                className={`h-full rounded-full transition-all ${
                  termStatus.all_results_entered ? "bg-emerald-500" : "bg-amber-500"
                }`}
                style={{ width: `${termStatus.completion_percentage}%` }}
              />
            </div>

            <div className="text-sm">
              {termStatus.all_results_entered ? (
                <span className="text-emerald-600 font-medium">
                  All students have results entered
                </span>
              ) : (
                <span className="text-amber-600 font-medium">
                  {termStatus.total_students - termStatus.students_with_results} students still missing results
                </span>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* End Term */}
        <Card className="border-slate-200 border-l-4 border-l-amber-500">
          <CardHeader>
            <CardTitle className="font-heading text-lg">End Term</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              End the current term and advance to the next. This will:
            </p>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>Finalize all {activeTerm} results</li>
              <li>Calculate student fee balances</li>
              <li>Carry overpayments to the next term</li>
              <li>Advance to Term {Math.min(termNum + 1, 3)}</li>
            </ul>
            <Button
              onClick={() => setShowEndTermDialog(true)}
              disabled={termNum >= 3 || !termStatus?.all_results_entered}
              className="w-full bg-amber-500 hover:bg-amber-600 text-white"
            >
              End {activeTerm}
            </Button>
            {termNum >= 3 && (
              <p className="text-xs text-slate-500">Term 3 is the last term. Use "End Year" instead.</p>
            )}
            {termStatus && !termStatus.all_results_entered && termNum < 3 && (
              <p className="text-xs text-amber-600">All students must have results before ending term.</p>
            )}
          </CardContent>
        </Card>

        {/* End Year */}
        <Card className="border-slate-200 border-l-4 border-l-red-500">
          <CardHeader>
            <CardTitle className="font-heading text-lg">End Academic Year</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-slate-600">
              End the academic year {academicYear}. This will:
            </p>
            <ul className="text-sm text-slate-600 space-y-1 list-disc list-inside">
              <li>Finalize all Term 3 results</li>
              <li>Calculate final fee balances</li>
              <li>Carry over/underpayments to {parseInt(academicYear) + 1}</li>
              <li>Reset to Term 1 of the new year</li>
            </ul>
            <Button
              onClick={() => setShowEndYearDialog(true)}
              disabled={activeTerm !== "Term 3" || !termStatus?.all_results_entered}
              className="w-full bg-red-500 hover:bg-red-600 text-white"
            >
              End Year {academicYear}
            </Button>
            {activeTerm !== "Term 3" && (
              <p className="text-xs text-slate-500">You must be on Term 3 to end the year.</p>
            )}
            {activeTerm === "Term 3" && termStatus && !termStatus.all_results_entered && (
              <p className="text-xs text-red-600">All students must have results before ending year.</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* End Term Confirmation */}
      <Dialog open={showEndTermDialog} onOpenChange={setShowEndTermDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Confirm End Term</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-slate-600">
              Are you sure you want to end <strong>{activeTerm}</strong>? This will:
            </p>
            <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
              <li>Move fee balances to Term {termNum + 1}</li>
              <li>Set active term to Term {termNum + 1}</li>
              <li>This action cannot be undone</li>
            </ul>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowEndTermDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleEndTerm}
                disabled={endingTerm}
                className="bg-amber-500 hover:bg-amber-600 text-white"
              >
                {endingTerm ? "Processing..." : `End ${activeTerm}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* End Year Confirmation */}
      <Dialog open={showEndYearDialog} onOpenChange={setShowEndYearDialog}>
        <DialogContent className="sm:max-w-[400px]">
          <DialogHeader>
            <DialogTitle className="font-heading">Confirm End Academic Year</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <p className="text-slate-600">
              Are you sure you want to end academic year <strong>{academicYear}</strong>?
            </p>
            <ul className="text-sm text-slate-600 list-disc list-inside space-y-1">
              <li>Carry all fee balances to {parseInt(academicYear) + 1}</li>
              <li>Reset to Term 1</li>
              <li>This action cannot be undone</li>
            </ul>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowEndYearDialog(false)}>
                Cancel
              </Button>
              <Button
                onClick={handleEndYear}
                disabled={endingYear}
                className="bg-red-500 hover:bg-red-600 text-white"
              >
                {endingYear ? "Processing..." : `End Year ${academicYear}`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
